import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FeatureCollection } from "@turf/turf";
import {
  AnimalsIcon,
  AnimalsPawIcon,
  DryMatterIcon,
  FieldsIcon,
  HectaresIcon,
} from "common/icons";
import AnimalGroupLocationIcon from "common/icons/AnimalGroupLocationIcon";
import { useTranslation } from "common/locales";
import {
  getAllFieldsForAnimalGroupId,
  getAnimalGroupColor,
  getAvailableDryMatterForAnimalGroupId,
  getTotalAnimalCount,
  selectAllAnimalGroups,
  useAppSelector,
} from "model";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { mixpanel } from "../../common/analytics";
import FieldMapPreview from "./FieldMapPreview";

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 8px 36px;
  margin: 0 -36px;
  gap: 16px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const AnimalCard = styled(Card)`
  min-width: 280px;
  max-width: 320px;
  cursor: pointer;
  transition: transform 0.2s;
  background-color: white;
  border-radius: 16px;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MetricsContainer = styled.div`
  gap: 16px;
  padding: 16px 0px;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const IconCircle = styled.div<{ color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

const MapPreviewContainer = styled.div`
  height: 120px;
  background: #f5f5f5;
  border-radius: 16px 16px 0 0;
  position: relative;
  overflow: hidden;
  margin: -16px -16px 16px -16px;
`;

const MapPlaceholderText = styled(Typography)`
  font-weight: 500;
  letter-spacing: 0.5px;
  opacity: 0.8;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface AnimalGroupCardProps {
  groupId: string;
  onCardClick: () => void;
}

const AnimalGroupCard: React.FC<AnimalGroupCardProps> = ({
  groupId,
  onCardClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconSize = isMobile ? "small" : "medium";

  const animalGroup = useAppSelector(
    (state) => state.animalGroups.entities[groupId]
  );
  const animalCount = useAppSelector((state) =>
    getTotalAnimalCount(state, groupId)
  );
  const dryMatterAvailable = Math.round(
    useAppSelector(getAvailableDryMatterForAnimalGroupId(groupId))
  );
  const fields = useAppSelector(getAllFieldsForAnimalGroupId(groupId));
  const currentField = fields.find(
    (f) => f.animalGroupNow === animalGroup?.guid
  );

  const groupColor = useAppSelector(getAnimalGroupColor(groupId));

  if (!animalGroup) return null;

  // Calculate field metrics
  const fieldCount = fields.length;
  const totalArea = fields.reduce((sum, field) => sum + (field.area || 0), 0);
  const totalAreaRounded = Math.round(totalArea / 1000) / 10;

  // Create field geojson for map preview
  const fieldGeojson: FeatureCollection = {
    type: "FeatureCollection",
    features: fields.map((field) => field.geom),
  };

  return (
    <AnimalCard onClick={onCardClick}>
      <CardContent>
        <MapPreviewContainer>
          {fields.length > 0 ? (
            <FieldMapPreview
              geojson={fieldGeojson}
              isArable={false}
              fieldIds={fields.map((field) => field.guid)}
            />
          ) : (
            <MapPlaceholderText variant="body2">No Fields</MapPlaceholderText>
          )}
        </MapPreviewContainer>

        <MetricRow>
          <IconCircle color={animalGroup.color || groupColor}>
            <AnimalsIcon fontSize="small" />
          </IconCircle>
          <Typography variant="body1" fontWeight="medium" gutterBottom>
            {animalGroup.name}
          </Typography>
        </MetricRow>

        <MetricsContainer>
          <MetricColumn>
            {currentField && (
              <MetricRow>
                <AnimalGroupLocationIcon fontSize={iconSize} />
                <Typography variant="body2">{currentField.name}</Typography>
              </MetricRow>
            )}

            {fieldCount > 0 && (
              <>
                <MetricRow>
                  <FieldsIcon fontSize={iconSize} />
                  <Typography variant="body2">
                    {fieldCount} {t("fieldTypeCards.metrics.fields")}
                  </Typography>
                </MetricRow>
                <MetricRow>
                  <HectaresIcon fontSize={iconSize} />
                  <Typography variant="body2">
                    {t("fieldTypeCards.metrics.hectares")}: {totalAreaRounded}
                  </Typography>
                </MetricRow>
              </>
            )}

            <MetricRow>
              <DryMatterIcon fontSize={iconSize} />
              <MetricColumn style={{ gap: 0 }}>
                <Typography variant="body2">
                  {dryMatterAvailable}{" "}
                  {t("animalGroup.metrics.dryMatterAvailableUnit")}
                </Typography>
                <Typography variant="body2">
                  {t("animalGroup.metrics.dryMatterAvailable")}
                </Typography>
              </MetricColumn>
            </MetricRow>

            <MetricRow>
              <AnimalsPawIcon fontSize={iconSize} />
              <Typography variant="body2">
                {animalCount} {t("animalGroup.metrics.animals")}
              </Typography>
            </MetricRow>
          </MetricColumn>
        </MetricsContainer>
      </CardContent>
    </AnimalCard>
  );
};

export default function AnimalGroupCards() {
  const history = useHistory();
  const animalGroups = useAppSelector(selectAllAnimalGroups);

  const handleCardClick = (groupId: string) => {
    mixpanel.track("Animal group card clicked", { groupId });
    history.push(`/animal-group/${groupId}`);
  };

  return (
    <Box>
      <ScrollContainer>
        {animalGroups.map((group) => (
          <AnimalGroupCard
            key={group.guid}
            groupId={group.guid}
            onCardClick={() => handleCardClick(group.guid)}
          />
        ))}
      </ScrollContainer>
    </Box>
  );
}
